import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';
import { ENTITY_QUERY_KEYS } from '../../../constants/queryKeys';
import type { EntityQueryKey } from '../../../types/types';
import { getBrowserTimezone } from '../../../utils/datetime';
import { invalidateEntityQueries } from '../../../utils/queryInvalidation';
import { FE_TOOLS } from '../constants';
import {
  ChatMessage,
  ChatMode,
  ModelProvider,
  StreamEvent,
  TextMessageEvent,
  ToolInvocationEvent,
} from '../types';
import { AgentContext } from '../../../types';

const toApiContext = (context?: AgentContext) => {
  if (!context) return undefined;
  return {
    environment_ids: context.environmentIds,
    incident_ids: context.incidentIds,
    asset_ids: context.assetIds,
    test_ids: context.testIds,
  };
};

const buildWsUrl = (
  api: string,
  params: Record<string, string | undefined>,
): string => {
  const base = api.replace(/^http/, 'ws');
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join('&');
  if (!query) return base;
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}${query}`;
};

enum EventTypes {
  NewMessage = 'f',
  TextMessage = '0',
  ToolInvocation = '9',
  ToolInvocationStart = 'b',
  ToolInvocationResult = 'a',
  ToolInvocationInteraction = 'z',
  ConversationEnd = 'd',
  DataChanged = 'g',
}

type Props = {
  api: string;
  selectedModel: ModelProvider;
  chatHistory?: {
    threadId: string;
    messages: ChatMessage[];
  };
  context?: AgentContext;
  mode?: ChatMode;
  enabled?: boolean;
};

const useChatStream = ({
  api,
  selectedModel,
  chatHistory,
  context,
  mode = 'agent',
  enabled = true,
}: Props) => {
  const [messages, setMessages] = useState<ChatMessage[]>(
    chatHistory?.messages || [],
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const conversationEndReceivedRef = useRef(false);
  const closeExpectedRef = useRef(false);
  const autoRetryAttemptedRef = useRef(false);
  const messagesRef = useRef<ChatMessage[]>(chatHistory?.messages || []);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const threadId = useMemo(
    () => chatHistory?.threadId || crypto.randomUUID(),
    [chatHistory?.threadId],
  );

  const feTools = useMemo(
    () =>
      FE_TOOLS.filter(
        (tool) => !tool.enabledModes || tool.enabledModes.includes(mode),
      ),
    [mode],
  );

  const stop = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || socket.readyState === WebSocket.CLOSED) return;

    closeExpectedRef.current = true;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    socket.close();
    socketRef.current = null;
    setIsLoading(false);
  }, []);

  const handleNewMessageEvent = () => {
    const newMessage: ChatMessage = {
      content: '',
      parts: [{ type: 'step-start' }],
      role: 'assistant',
      toolInvocations: [],
    };

    setMessages((prev) => [...prev, newMessage]);
  };

  const handleTextMessageEvent = (payload: string) => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      const lastPart = lastMessage.parts[
        lastMessage.parts.length - 1
      ] as StreamEvent;
      const parsedPayload: string = JSON.parse(payload);

      if (lastPart.type === 'text') {
        const updatedParts = [...lastMessage.parts];
        updatedParts[updatedParts.length - 1] = {
          ...lastPart,
          text: lastPart.text + parsedPayload,
        } as TextMessageEvent;

        const updatedMessage = {
          ...lastMessage,
          content: lastMessage.content + parsedPayload,
          parts: updatedParts,
          toolInvocations: lastMessage.toolInvocations || [],
        };
        return [...prev.slice(0, -1), updatedMessage];
      }

      const updatedMessage = {
        ...lastMessage,
        parts: [
          ...lastMessage.parts,
          { type: 'text', text: parsedPayload } as TextMessageEvent,
        ],
        toolInvocations: lastMessage.toolInvocations || [],
      };
      return [...prev.slice(0, -1), updatedMessage];
    });
  };

  const handleToolInvocationEvent = (prefix: EventTypes, payload: string) => {
    const parsedPayload = JSON.parse(
      payload,
    ) as ToolInvocationEvent['toolInvocation'];

    const toolCallState = parsedPayload.result ? 'result' : 'call';

    const toolCallType =
      prefix === EventTypes.ToolInvocationInteraction
        ? parsedPayload.type === 'output'
          ? 'output'
          : 'approval'
        : prefix === EventTypes.ToolInvocationStart
        ? 'start'
        : undefined;

    if (toolCallType === 'output') {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    setMessages((prev) => {
      const existingCallMessageIndex = prev.findIndex((message) =>
        message.parts.find(
          (part) =>
            part.type === 'tool-invocation' &&
            part.toolInvocation.toolCallId === parsedPayload.toolCallId,
        ),
      );

      if (existingCallMessageIndex >= 0) {
        const updatedParts = prev[existingCallMessageIndex].parts.map(
          (part) => {
            if (
              part.type === 'tool-invocation' &&
              part.toolInvocation.toolCallId === parsedPayload.toolCallId
            ) {
              return {
                ...part,
                toolInvocation: {
                  ...part.toolInvocation,
                  ...parsedPayload,
                  state: toolCallState,
                  type: toolCallType,
                },
              } as ToolInvocationEvent;
            }
            return part;
          },
        );
        return prev.map((message, index) => {
          if (index === existingCallMessageIndex) {
            return {
              ...message,
              parts: updatedParts,
              toolInvocations:
                message.toolInvocations?.map((invocation) =>
                  invocation.toolCallId === parsedPayload.toolCallId
                    ? {
                        ...invocation,
                        ...parsedPayload,
                        state: toolCallState,
                      }
                    : invocation,
                ) || [],
            };
          }
          return message;
        });
      }

      const lastMessage = prev[prev.length - 1];
      const toolEvent: ToolInvocationEvent = {
        type: 'tool-invocation',
        toolInvocation: {
          ...parsedPayload,
          state: toolCallState,
          type: toolCallType,
        },
      };

      const updatedMessage = {
        ...lastMessage,
        parts: [...lastMessage.parts, toolEvent],
        toolInvocations: [
          ...(lastMessage.toolInvocations || []),
          parsedPayload,
        ],
      };
      return [...prev.slice(0, -1), updatedMessage];
    });
  };

  const handleDataChangedEvent = useCallback(
    (payload: string) => {
      const { entities } = JSON.parse(payload) as { entities: string[] };
      const knownEntities = entities.filter(
        (entity): entity is EntityQueryKey => entity in ENTITY_QUERY_KEYS,
      );
      void invalidateEntityQueries(queryClient, knownEntities);
    },
    [queryClient],
  );

  const handleConversationEndEvent = (payload: string) => {
    conversationEndReceivedRef.current = true;
    autoRetryAttemptedRef.current = false;
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      const updatedMessage = {
        ...lastMessage,
        parts: [
          ...lastMessage.parts,
          { type: 'conversation-end', ...JSON.parse(payload) },
        ],
      };

      return [...prev.slice(0, -1), updatedMessage];
    });
  };

  const processStreamLine = useCallback(
    (line: string) => {
      const prefix = line.slice(0, 1);
      const payload = line.slice(2).trim();

      try {
        switch (prefix) {
          case EventTypes.NewMessage: {
            handleNewMessageEvent();
            break;
          }
          case EventTypes.TextMessage: {
            handleTextMessageEvent(payload);
            break;
          }
          case EventTypes.ToolInvocation:
          case EventTypes.ToolInvocationStart:
          case EventTypes.ToolInvocationResult:
          case EventTypes.ToolInvocationInteraction: {
            handleToolInvocationEvent(prefix, payload);
            break;
          }
          case EventTypes.ConversationEnd: {
            handleConversationEndEvent(payload);
            break;
          }
          case EventTypes.DataChanged: {
            handleDataChangedEvent(payload);
            break;
          }
          default:
            break;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to process stream event', err);
      }
    },
    [handleDataChangedEvent],
  );

  const connectAndSend = useCallback(
    (messagesToSend: ChatMessage[]) => {
      if (!enabled) return;
      // Stop any ongoing connection before starting a new one
      stop();

      // Reset error state and set loading to true
      setError(null);
      setIsLoading(true);
      conversationEndReceivedRef.current = false;
      closeExpectedRef.current = false;

      // Create a new WebSocket connection
      const wsUrl = buildWsUrl(api, {
        thread_id: threadId,
        mode,
        tz: getBrowserTimezone(),
      });
      socketRef.current = new WebSocket(wsUrl, [
        'aichat',
        `Authorization.${user?.accessToken}`,
      ]);

      socketRef.current.onopen = () => {
        // Send the message once the connection is established
        if (
          socketRef.current &&
          socketRef.current.readyState === WebSocket.OPEN
        ) {
          socketRef.current.send(
            JSON.stringify({
              messages: messagesToSend,
              model: selectedModel,
              tools: feTools,
              context: toApiContext(context),
            }),
          );
        }
      };

      socketRef.current.onmessage = (event) => {
        setIsLoading(true);

        const chunk = event.data;
        const streamLines = chunk.split('\n');

        streamLines
          .filter((line: string) => !!line)
          .forEach((line: string) => processStreamLine(line));
      };

      socketRef.current.onerror = () => {
        if (!conversationEndReceivedRef.current) {
          setError('WebSocket error occurred');
        }
        stop();
      };

      socketRef.current.onclose = () => {
        if (conversationEndReceivedRef.current || closeExpectedRef.current) {
          setIsLoading(false);
          return;
        }
        if (autoRetryAttemptedRef.current) {
          setError('Connection closed unexpectedly');
          setIsLoading(false);
          return;
        }
        autoRetryAttemptedRef.current = true;
        const continueMessage: ChatMessage = {
          content: 'continue',
          role: 'user',
          parts: [{ type: 'text', text: 'continue' }],
          hidden: true,
        };
        const updated = [...messagesRef.current, continueMessage];
        setMessages(updated);
        connectAndSend(updated);
      };
    },
    [
      enabled,
      stop,
      api,
      threadId,
      user?.accessToken,
      selectedModel,
      feTools,
      context,
      mode,
      processStreamLine,
    ],
  );

  const sendMessage = useCallback(
    (message: string, messageContext?: AgentContext) => {
      const userMessage: ChatMessage = {
        content: message,
        role: 'user',
        parts: [{ type: 'text', text: message }],
        ...(messageContext && { context: messageContext }),
      };

      autoRetryAttemptedRef.current = false;
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);

      connectAndSend(updatedMessages);
    },
    [messages, connectAndSend],
  );

  const approveToolUse = useCallback(
    (
      approved: boolean,
      interruptId?: string,
      newArgs?: Record<string, unknown>,
    ) => {
      const payload: Record<string, unknown> = { approved };
      if (interruptId) payload.interruptId = interruptId;
      if (newArgs !== undefined && Object.keys(newArgs).length > 0) {
        payload.new_args = newArgs;
      }
      socketRef.current?.send(JSON.stringify(payload));
    },
    [],
  );

  const userInteraction = useCallback((val: Record<string, any>) => {
    socketRef.current?.send(JSON.stringify({ output: val }));
  }, []);

  const continueChat = useCallback(() => {
    autoRetryAttemptedRef.current = false;
    connectAndSend(messages);
  }, [connectAndSend, messages]);

  // Clean up the WebSocket connection when the component unmounts
  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    if (!enabled) {
      closeExpectedRef.current = true;
      stop();
    }
  }, [enabled, stop]);

  useEffect(() => {
    if (!enabled) return;
    if (chatHistory?.messages.length) {
      closeExpectedRef.current = true;
      stop();
      setMessages(chatHistory?.messages);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory, enabled]);

  // Consolidate multiple assistant messages in a row to one message
  const consolidatedMessages = useMemo(
    () =>
      messages
        .filter((message) => !message.hidden)
        .reduce<ChatMessage[]>((result, message) => {
          const lastMessage = result[result.length - 1];

          if (
            message.role === 'assistant' &&
            lastMessage?.role === 'assistant'
          ) {
            // Merge with previous assistant message
            lastMessage.parts.push(...message.parts);
            return result;
          } else {
            // Add new message (create copy to avoid mutation)
            result.push({ ...message, parts: [...message.parts] });
            return result;
          }
        }, []),
    [messages],
  );

  return {
    threadId,
    sendMessage,
    messages,
    consolidatedMessages,
    stop,
    error,
    isLoading,
    continueChat,
    approveToolUse,
    userInteraction,
  };
};

export default useChatStream;
